import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

/**
 * Stimulus controller for inline editing of a single field via PATCH request.
 *
 * Watches an input for changes and sends updates to the server without a full
 * page reload. Also hooks into a parent form's submit event so any unsaved
 * inline edits are flushed before the form submits.
 *
 * @example
 *   <div data-controller="inline-edit"
 *        data-inline-edit-url-value="/courses/1"
 *        data-inline-edit-field-value="course">
 *     <input data-inline-edit-target="input" name="course[name]">
 *     <button data-inline-edit-target="button">Save</button>
 *   </div>
 */
export default class extends Controller {
  static targets = ["input", "button"]
  static values = {
    url: String,
    field: String
  }

  /**
   * Stores the original input value and attaches a submit listener to
   * the nearest parent form so dirty changes are saved on form submission.
   */
  connect() {
    this.originalValue = this.inputTarget.value
    this._form = this.element.closest('form')
    if (this._form) {
      this._handleFormSubmit = this._submitDirty.bind(this)
      this._form.addEventListener('submit', this._handleFormSubmit)
    }
  }

  /**
   * Cleans up the parent form submit listener.
   */
  disconnect() {
    if (this._form && this._handleFormSubmit) {
      this._form.removeEventListener('submit', this._handleFormSubmit)
    }
  }

  /**
   * Handles keyboard shortcuts on the input.
   * Enter saves the value; Escape reverts to the original value.
   * @param {KeyboardEvent} event
   */
  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.save()
    } else if (event.key === "Escape") {
      this.inputTarget.value = this.originalValue
      this.inputTarget.blur()
    }
  }

  /**
   * Whether the current input value differs from the original.
   * @returns {boolean}
   */
  get dirty() {
    return this.inputTarget.value !== this.originalValue
  }

  /**
   * Saves the field if dirty when the parent form is submitted.
   * Uses `keepalive` so the request completes even during navigation.
   * @private
   */
  _submitDirty() {
    if (this.dirty && !this.saving) {
      this.save({ keepalive: true })
    }
  }

  /**
   * Sends a PATCH request to persist the changed value. Shows a spinner
   * while saving and a checkmark on success. Turbo Stream responses are
   * rendered so the server can push flash toasts.
   * @param {RequestInit} [fetchOptions={}] - Additional options passed to fetch
   */
  async save(fetchOptions = {}) {
    if (this.saving) return
    if (!this.dirty) return
    this.saving = true

    const button = this.buttonTarget
    const input = this.inputTarget
    const originalHTML = button.innerHTML

    button.disabled = true
    button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const body = {}
      body[this.fieldValue] = { [input.name.match(/\[(\w+)\]$/)?.[1] || input.name]: input.value }

      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify(body),
        ...fetchOptions
      })

      const html = await response.text()

      if (response.ok) {
        this.originalValue = input.value
        button.innerHTML = '<i class="fas fa-check text-success"></i>'
        setTimeout(() => {
          button.innerHTML = originalHTML
          button.disabled = false
          this.saving = false
        }, 1500)
      } else {
        button.innerHTML = originalHTML
        button.disabled = false
        this.saving = false
      }

      // Let Turbo process the stream response for flash toasts
      if (html && response.headers.get("content-type")?.includes("turbo-stream")) {
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      button.innerHTML = originalHTML
      button.disabled = false
      this.saving = false
    }
  }
}
